// RearrangedQuadraticInequalityTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/* ===== helpers ===== */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

function gcd(left, right) {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

const gcd3 = (a, b, c) => gcd(gcd(a, b), c);

function quadraticLatex(a, b, c) {
  const ax = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  const bx =
    b === 0 ? "" : b === 1 ? "+ x" : b === -1 ? "- x" : b > 0 ? `+ ${b}x` : `- ${Math.abs(b)}x`;
  const cx = c === 0 ? "" : c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`;
  return `${ax} ${bx} ${cx}`.replace(/\s+/g, " ").trim();
}
function linearLatex(p, q) {
  const px =
    p === 0 ? "" : p === 1 ? "x" : p === -1 ? "-x" : p > 0 ? `${p}x` : `-${Math.abs(p)}x`;
  const qq = q === 0 ? "" : q > 0 ? `+ ${q}` : `- ${Math.abs(q)}`;
  const s = `${px} ${qq}`.replace(/\s+/g, " ").trim();
  return s.length ? s : "0";
}

/* ===== generator =====
   Tworzymy nierówność: (ax^2 + bx + c) [znak] (px + q),
   a po przeniesieniu wszystkiego na lewą stronę dostajemy kwadrat z Δ>0 i pierwiastkiem całkowitym.
*/
export function generateTask(options) {
  const forceKind = typeof options === "string" ? options : options?.kind;
  const randi = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  let a = 0;
  while (a === 0 || Math.abs(a) === 1) a = randi(-5, 5); // nie 0 i nie ±1

  let r1, r2, B, C;
  do {
    r1 = randi(-6, 5);
    r2 = randi(r1 + 1, 6);
    B = -a * (r1 + r2);
    C = a * r1 * r2;
  } while (B === 0 || C === 0 || Math.abs(B) > 24 || Math.abs(C) > 60);

  const disc = B * B - 4 * a * C;
  const sqrtDisc = Math.trunc(Math.sqrt(disc));
  const commonDivisor = gcd3(a, B, C);
  const reducedA = a / commonDivisor;
  const reducedB = B / commonDivisor;
  const reducedC = C / commonDivisor;
  const reducedDisc = reducedB * reducedB - 4 * reducedA * reducedC;
  const reducedSqrtDisc = Math.trunc(Math.sqrt(reducedDisc));

  const p = randi(-6, 6);
  const q = randi(-10, 10);
  const b = B + p;
  const c = C + q;

  const kind = forceKind ?? SIGN_KINDS[randi(0, SIGN_KINDS.length - 1)];

  const leftLatex = quadraticLatex(a, b, c);
  const rightLatex = linearLatex(p, q);
  const prettyLatex = `${leftLatex}\\;${SIGN_LATEX[kind]}\\;${rightLatex}`;
  const standardLatex = `${quadraticLatex(a, B, C)}\\;${SIGN_LATEX[kind]}\\;0`;
  const reducedLatex = `${quadraticLatex(reducedA, reducedB, reducedC)}\\;${SIGN_LATEX[kind]}\\;0`;

  return {
    type: "RearrangedQuadraticInequalityTask",
    data: {
      a, b, c, p, q,
      B, C, disc, sqrtDisc, r1, r2,
      kind, prettyLatex, standardLatex, reducedLatex,
      commonDivisor, reducedA, reducedB, reducedC, reducedDisc, reducedSqrtDisc,
    },
  };
}

/* ===== UI ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <InlineMath math={data.prettyLatex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={'x\\;\\in'} />
        </div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 1) ∪ (4, ∞) albo (1, 4)"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw =
    typeof userInput === "string"
      ? userInput
      : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== logika rozwiązania ===== */
function solutionFor({ a, r1, r2, kind }) {
  const inclusive = kind === "le" || kind === "ge";
  const needOutside = (kind === "gt" || kind === "ge") ? a > 0 : a < 0;
  if (needOutside) {
    return {
      kind: "outside",
      inclusive,
      left:  { L: -Infinity, R: r1, Rclosed: inclusive },
      right: { L: r2,        R: Infinity, Lclosed: inclusive },
    };
  }
  return {
    kind: "between",
    inclusive,
    seg: { L: r1, R: r2, Lclosed: inclusive, Rclosed: inclusive },
  };
}

function intervalsEqual(ans, parsed) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const eq = (x, y, tol = 1e-9) => Math.abs(x - y) <= tol;
  const norm = (I) => ({
    L: I.left, R: I.right, Lc: !!I.leftClosed, Rc: !!I.rightClosed,
  });

  const expected = [];
  if (ans.kind === "outside") {
    expected.push({ L: -Infinity, R: ans.left.R,  Lc: false, Rc: !!ans.left.Rclosed });
    expected.push({ L: ans.right.L, R: Infinity, Lc: !!ans.right.Lclosed, Rc: false });
  } else {
    expected.push({ L: ans.seg.L, R: ans.seg.R, Lc: !!ans.seg.Lclosed, Rc: !!ans.seg.Rclosed });
  }

  const got = parsed.intervals.map(norm);
  if (expected.length !== got.length) return false;

  const used = new Array(got.length).fill(false);
  for (const E of expected) {
    let ok = false;
    for (let i = 0; i < got.length; i++) {
      if (used[i]) continue;
      const G = got[i];
      const Lok = E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L);
      const Rok = E.R ===  Infinity ? G.R ===  Infinity : eq(E.R, G.R);
      if (Lok && Rok && E.Lc === G.Lc && E.Rc === G.Rc) { used[i] = true; ok = true; break; }
    }
    if (!ok) return false;
  }
  return true;
}

export function checkAnswer({ data }, userInput = {}) {
  const raw =
    typeof userInput?.S === "string"
      ? userInput.S
      : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionFor({ a: data.a, r1: data.r1, r2: data.r2, kind: data.kind });
  return { correct: intervalsEqual(ans, parsed) };
}

/* ===== poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const ans = solutionFor(data);
  const { r1, r2 } = data;
  const R1 = formatMixedFractionLatex(r1);
  const R2 = formatMixedFractionLatex(r2);
  const rb = ans.inclusive ? "]" : ")";
  const lb = ans.inclusive ? "[" : "(";
  if (ans.kind === "outside") {
    return (
      <InlineMath math={`x\\in (-\\infty,\\; ${R1}${rb}\\;\\cup\\; ${lb}${R2},\\; \\infty)`} />
    );
  }
  return <InlineMath math={`x\\in ${lb}${R1},\\; ${R2}${rb}`} />;
}

export function renderExplanation({ data }) {
  const {
    a, r1, r2, kind, prettyLatex, standardLatex, reducedLatex,
    commonDivisor, reducedA, reducedB, reducedC, reducedDisc, reducedSqrtDisc,
  } = data;
  const ans = solutionFor({ a, r1, r2, kind });

  const aPos = a > 0;
  const armsTxt = aPos ? "ramiona do góry" : "ramiona w dół";
  const chooseTxt =
    kind === "gt" || kind === "ge"
      ? "bierzemy część wykresu powyżej osi OX"
      : "bierzemy część wykresu poniżej osi OX";
  const inclusiveNote = ans.inclusive
    ? "Ponieważ znak jest „≤/≥”, miejsca zerowe także należą do rozwiązania."
    : "Miejsca zerowe nie należą do rozwiązania (otwarte końce).";

  const R1_ltx = formatMixedFractionLatex(r1);
  const R2_ltx = formatMixedFractionLatex(r2);

  // zapisy liczb ujemnych w nawiasach w podstawieniach
  const num = (n) => (n < 0 ? `(${n})` : `${n}`);

  // do obliczeń pierwiastków
  const minusBLatex = reducedB < 0 ? `-(${reducedB})` : `-${reducedB}`;
  const denom = 2 * reducedA;
  const denomFormulaLatex = `2\\cdot ${num(reducedA)}`;
  const rootVariants = [
    {
      sign: "-",
      numerator: -reducedB - reducedSqrtDisc,
      value: (-reducedB - reducedSqrtDisc) / denom,
    },
    {
      sign: "+",
      numerator: -reducedB + reducedSqrtDisc,
      value: (-reducedB + reducedSqrtDisc) / denom,
    },
  ].sort((left, right) => left.value - right.value);

  const rootLine = (label, root, resultLatex) => String.raw`
\begin{aligned}
${label} &= \dfrac{${minusBLatex} ${root.sign} ${reducedSqrtDisc}}{${denomFormulaLatex}} \\[4pt]
${label} &= \dfrac{${root.numerator}}{${denom}} \\[4pt]
${label} &= ${resultLatex}
\end{aligned}
`;

  const x1Lines = rootLine("x_1", rootVariants[0], R1_ltx);
  const x2Lines = rootLine("x_2", rootVariants[1], R2_ltx);

  const solLatex =
    ans.kind === "outside"
      ? String.raw`x\in (-\infty,\; ${R1_ltx}${ans.inclusive ? "]" : ")"}\ \cup\ ${ans.inclusive ? "[" : "("}${R2_ltx},\; \infty)`
      : String.raw`x\in ${ans.inclusive ? "[" : "("}${R1_ltx},\; ${R2_ltx}${ans.inclusive ? "]" : ")"}`;

  // KROK 1 – przeniesienie na jedną stronę (poprawione \quad)
  // Uwaga: pojedynczy backslash przy \quad, żeby KaTeX nie pisał "quad".
  const step1Eq = String.raw`${prettyLatex}\quad \Longleftrightarrow \quad ${standardLatex}`;

  return (
    <div className="space-y-3">
      {/* KROK 1 */}
      <p><strong>Krok 1:</strong> Przenosimy wszystko na jedną stronę.</p>
      <BlockMath math={step1Eq} />

      <p>
        <strong>Krok 2:</strong> Skracamy współczynniki przez ich wspólny
        dodatni dzielnik <InlineMath math={`${commonDivisor}`} />. Znak
        nierówności pozostaje bez zmian.
      </p>
      <BlockMath math={`${standardLatex}\\quad |:${commonDivisor}\\quad\\Longleftrightarrow\\quad ${reducedLatex}`} />

      <p><strong>Krok 3:</strong> Liczymy miejsca zerowe z delty.</p>
      <BlockMath math={String.raw`\Delta = ${num(reducedB)}^2 - 4\cdot ${num(reducedA)}\cdot ${num(reducedC)} = ${reducedDisc}`} />
      <BlockMath math={String.raw`\sqrt{\Delta} = ${reducedSqrtDisc}`} />

      {/* równoległe obliczenia x1 i x2 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_1'} />
          </div>
          <BlockMath math={x1Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_2'} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
      </div>

      <p>
        <strong>Krok 4:</strong> Rysujemy uproszczony wykres (
        <InlineMath math={`a=${a}`} /> — {aPos ? "dodatni" : "ujemny"}{" "}
        <InlineMath math={aPos ? "a>0" : "a<0"} /> → {armsTxt}
        ) i patrzymy, gdzie spełniona jest{" "}
        <InlineMath math={reducedLatex} /> — {chooseTxt}. {inclusiveNote}
      </p>
      <div style={{ maxWidth: 620, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={aPos ? 1 : -1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          labelLeftTex={R1_ltx}
          labelRightTex={R2_ltx}
          highlightMode={ans.kind === "outside" ? "outside" : "between"}
          highlightLeftClosed={ans.inclusive}
          highlightRightClosed={ans.inclusive}
          highlightColor="#a78bfa"
        />
      </div>

      <p><strong>Krok 5:</strong> Zapisujemy odpowiedź i zaznaczamy ją na osi liczbowej.</p>
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <InlineMath math={solLatex} />
      </div>

      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        {ans.kind === "outside" ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to: r1, rightClosed: ans.inclusive, rightLabelLatex: R1_ltx },
              { from: r2, leftClosed: ans.inclusive, leftLabelLatex: R2_ltx },
            ]}
          />
        ) : (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              {
                from: r1,
                to: r2,
                leftClosed: ans.inclusive,
                rightClosed: ans.inclusive,
                leftLabelLatex: R1_ltx,
                rightLabelLatex: R2_ltx,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}

